import { useState, useEffect, useCallback } from 'react';
import { tarjetaControlApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from '../../hooks/use-toast';
import { AlertTriangle, CheckCircle, Clock, Users, Save, Loader2 } from 'lucide-react';

export default function ControlErroresPage() {
  const now = new Date();
  const [mes, setMes] = useState(`${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getFullYear()).slice(2)}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingNota, setEditingNota] = useState(null);
  const [notaText, setNotaText] = useState('');
  const [savingNota, setSavingNota] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await tarjetaControlApi.getErrores({ mes });
      setData(res.data);
    } catch {
      toast({ title: 'Error al cargar comparacion', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [mes]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleMonthChange = (e) => {
    const v = e.target.value;
    if (v) {
      const [y, m] = v.split('-');
      setMes(`${m}/${y.slice(2)}`);
    }
  };

  const handleSaveNota = async (num) => {
    setSavingNota(true);
    try {
      await tarjetaControlApi.saveNota({ trabajador_numero: num, mes, nota: notaText });
      toast({ title: 'Nota guardada' });
      setEditingNota(null);
      loadData();
    } catch {
      toast({ title: 'Error al guardar nota', variant: 'destructive' });
    } finally {
      setSavingNota(false);
    }
  };

  if (loading && !data) return <TableSkeleton rows={10} cols={8} />;

  const d = data || { comparaciones: [], con_diferencia: 0, total_trabajadores: 0, total_diferencia_abs: 0 };

  return (
    <div className="space-y-4 page-enter" data-testid="control-errores">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Control de Errores</h1>
          <p className="text-sm text-muted-foreground">Comparacion Tarjeta Control vs HH cargadas a OTs — {mes}</p>
        </div>
        <input type="month" onChange={handleMonthChange} className="h-9 rounded-xl border bg-background px-3 text-sm" data-testid="select-mes" />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-3 flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-primary/10"><Users className="h-4 w-4 text-primary" /></div>
          <div><p className="text-[10px] text-muted-foreground">Trabajadores</p><p className="text-base font-bold tabular-nums">{d.total_trabajadores}</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-3 flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${d.con_diferencia > 0 ? 'bg-red-500/10' : 'bg-emerald-500/10'}`}>
            {d.con_diferencia > 0 ? <AlertTriangle className="h-4 w-4 text-red-600" /> : <CheckCircle className="h-4 w-4 text-emerald-600" />}
          </div>
          <div><p className="text-[10px] text-muted-foreground">Con Diferencia</p><p className="text-base font-bold tabular-nums">{d.con_diferencia}</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-3 flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-amber-500/10"><Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" /></div>
          <div><p className="text-[10px] text-muted-foreground">Diferencia Abs.</p><p className="text-base font-bold tabular-nums">{d.total_diferencia_abs}h</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-3 flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-emerald-500/10"><CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /></div>
          <div><p className="text-[10px] text-muted-foreground">Sin Diferencia</p><p className="text-base font-bold tabular-nums">{d.total_trabajadores - d.con_diferencia}</p></div>
        </CardContent></Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N</TableHead>
                  <TableHead>Trabajador</TableHead>
                  <TableHead className="text-right">Tarjeta HH</TableHead>
                  <TableHead className="text-right">OTs HH</TableHead>
                  <TableHead className="text-right">Diferencia</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Nota/Explicacion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {d.comparaciones.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Sin datos. Ingrese tarjetas y cargue HH para comparar.</TableCell></TableRow>
                ) : d.comparaciones.map((c) => {
                  const hasDiff = Math.abs(c.diferencia) > 0.1;
                  return (
                    <TableRow key={c.trabajador_numero} className={`hover:bg-muted/30 transition-colors ${hasDiff && !c.nota ? 'bg-red-500/5' : ''}`} data-testid={`error-row-${c.trabajador_numero}`}>
                      <TableCell className="font-mono text-sm tabular-nums">{c.trabajador_numero}</TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{c.nombre}</p>
                        <div className="flex gap-1 mt-0.5">
                          {!c.tiene_tarjeta && <span className="text-[10px] px-1 rounded bg-amber-500/10 text-amber-600">Sin Tarjeta</span>}
                          {!c.tiene_hh && <span className="text-[10px] px-1 rounded bg-blue-500/10 text-blue-600">Sin HH</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{c.tarjeta_total || '-'}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{c.hh_total || '-'}</TableCell>
                      <TableCell className="text-right">
                        <span className={`text-sm font-bold tabular-nums px-1.5 py-0.5 rounded-full ${
                          Math.abs(c.diferencia) < 0.1 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                          : c.diferencia > 0 ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                          : 'bg-red-500/10 text-red-600 dark:text-red-400'
                        }`}>
                          {c.diferencia > 0 ? '+' : ''}{c.diferencia}
                        </span>
                      </TableCell>
                      <TableCell>
                        {!hasDiff ? (
                          <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> OK</span>
                        ) : c.nota ? (
                          <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Explicado</span>
                        ) : (
                          <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Pendiente</span>
                        )}
                      </TableCell>
                      <TableCell className="min-w-[200px]">
                        {editingNota === c.trabajador_numero ? (
                          <div className="flex items-center gap-1">
                            <Input value={notaText} onChange={(e) => setNotaText(e.target.value)} className="h-7 text-xs rounded" placeholder="Explicacion..." autoFocus />
                            <Button size="icon" onClick={() => handleSaveNota(c.trabajador_numero)} disabled={savingNota} className="h-7 w-7 rounded shrink-0">
                              {savingNota ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            </Button>
                          </div>
                        ) : (
                          <button onClick={() => { setEditingNota(c.trabajador_numero); setNotaText(c.nota || ''); }} className="text-xs text-left hover:underline w-full">
                            {c.nota || (hasDiff ? <span className="text-muted-foreground italic">Click para explicar...</span> : '-')}
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
